/**
 * The journey graphic: two endpoints, a connector, intermediate stops and a
 * centre measure.
 *
 * It is the generalisation of `CflightcardJourney`, which is live-verified and
 * whose arc geometry is reproduced here exactly. What changes is everything
 * that was hardcoded to one pillar. The glyph on the connector is a PROP: the
 * same graphic carries a plane for flights and a car, a train or a walking
 * figure for route, and a component that hardcodes a plane is a component the
 * second consumer has to fork. The centre measure is a prop for the same
 * reason — a flight's duration and a route's distance occupy the same slot and
 * are not the same fact. The summary line is a prop for the third time: the kit
 * can count stops for anyone, but the sentence a journey with NO stops deserves
 * is domain copy, and "Direct" printed under a driving route was this component
 * asserting a flight's vocabulary over its second consumer's data.
 *
 * Three rules survive from the exemplar. Stops are capped at
 * `CRC_MAX_GRAPHIC_STOPS`, because a fourth node on a 200-unit arc lands on top
 * of the third; the ones past the cap are not dropped but folded into the
 * summary line, so a six-stop itinerary still says six. The graphic itself is
 * `aria-hidden` and the endpoints are real text, so a screen reader gets the
 * journey rather than a description of a curve. And the arrival day offset is a
 * superscript on the arrival, never a separate line: a `+1` that drifts away
 * from the time it modifies is a `+1` nobody connects to anything.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
// The thumb's centring and the fill's logical origin are stylesheet rules now,
// so this file no longer works without the stylesheet reaching the page.
import "./Cresultcard.css";
import {
  CRC_ACCENT,
  CRC_CORE_BACKGROUND_COLOR,
  CRC_NUMERIC_STYLE,
  CRC_STACK_GAP,
  CRC_TEXT,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
  CRC_TRUNCATE_STYLE,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardAlign,
  CresultcardJourneyShape,
  CresultcardJourneySummarySource,
  CresultcardStackDensity,
  CresultcardTextStep,
  type CresultcardJourneySummarySpec,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";

/** A fourth node on the arc lands on top of the third. The rest are folded. */
export const CRC_MAX_GRAPHIC_STOPS = 3;

/* ── Geometry ─────────────────────────────────────────────────────────────── */

/**
 * The live-verified arc, unchanged: a quadratic from (5,46) through (100,0) to
 * (195,46) in a 200×52 box. The two position helpers below are that curve
 * solved rather than approximated —
 *   x(f) = (5(1−f)² + 200f(1−f) + 195f²) / 2  =  2.5 + 95f
 *   y(f) = 46(1 − 2f + 2f²) / 52              =  88.46 − 176.9·f(1−f)
 * — so a node at fraction f sits ON the stroke at every f, not only at the
 * three points where a linear x approximation happens to agree with it.
 */
const ARC_VIEWBOX = "0 0 200 52";
const ARC_PATH = "M 5 46 Q 100 0 195 46";
const ARC_X_BASE = 2.5;
const ARC_X_SPAN = 95;
const ARC_Y_BASE = 88.46;
const ARC_Y_LIFT = 176.9;
const ARC_HEIGHT = 52;
const ARC_HEIGHT_COMPACT = 42;

const LINE_HEIGHT = 22;
const LINE_HEIGHT_COMPACT = 18;
const CONNECTOR_WIDTH = 1;
const CONNECTOR_STROKE = 1.3;
/** The stroke must not thicken when the arc box is stretched to the card. */
const NON_SCALING_STROKE = "non-scaling-stroke";

const RAIL_WIDTH = 14;
const RUNG_GAP = 9;
const THUMB_SIZE = 15;
const THUMB_SIZE_COMPACT = 13;
const THUMB_PAD = 2;
const CENTRE_GLYPH_SIZE = 13;
const GLYPH_STROKE = 1.5;
const NODE_RING_WIDTH = 1.5;
const PERCENT = 100;
const POSITION_PRECISION = 2;
const CODE_GAP = 2;
const CENTRE_GAP = 5;
const ENDPOINT_GAP = 10;

/**
 * The thumb's centring lives in the stylesheet, not in a style object.
 *
 * It is the one value on this graphic a family reaches for: a pillar whose glyph
 * travels along the connector on hover has to replace the transform, and an
 * inline transform can only be replaced with `!important`. As a class rule it is
 * outranked by ordinary specificity, which is how a family's own motion gets to
 * be a stylesheet rather than an override of one.
 */
const THUMB_CLASS = "cresultcard__journey-thumb";

/* ── Copy ─────────────────────────────────────────────────────────────────── */

const SUMMARY_STOP = "stop";
const SUMMARY_STOPS = "stops";
const SUMMARY_MORE = "more";
const SUMMARY_SEPARATOR = " · ";
const SUMMARY_JOIN = ", ";
const SUMMARY_PLUS = "+";
const LABEL_TO = " to ";
const DAY_OFFSET_PLUS = "+";
const EMPTY_CODE = "—";

/* ── Public shapes ────────────────────────────────────────────────────────── */

export interface CresultcardJourneyEndpoint {
  readonly code: string;
  readonly name?: string;
  readonly time?: string;
  readonly date?: string;
  readonly sublabel?: string;
}

export interface CresultcardJourneyStop {
  readonly code: string;
  readonly name?: string;
  readonly duration?: string;
}

/** The measure that belongs to the whole journey: a duration, a distance. */
export interface CresultcardJourneyCentre {
  readonly glyph: TablerIcon;
  readonly primary: string;
  readonly secondary?: string;
}

/** The moving glyph, and where along the connector it sits (0–1). */
export interface CresultcardJourneyThumb {
  readonly glyph: TablerIcon;
  readonly fraction: number;
}

/** A round trip closes back onto its origin; the ring node says so. */
export interface CresultcardJourneyClosing {
  readonly label: string;
}

/* ── Nodes ────────────────────────────────────────────────────────────────── */

enum JourneyNodeKind {
  Endpoint = "endpoint",
  Stop = "stop",
  Ring = "ring",
}

const NODE_DIAMETER: Record<JourneyNodeKind, number> = {
  [JourneyNodeKind.Endpoint]: 7,
  [JourneyNodeKind.Stop]: 5,
  [JourneyNodeKind.Ring]: 9,
};

const NODE_FILL: Record<JourneyNodeKind, string> = {
  [JourneyNodeKind.Endpoint]: CRC_ACCENT,
  [JourneyNodeKind.Stop]: CRC_CORE_BACKGROUND_COLOR,
  [JourneyNodeKind.Ring]: CRC_CORE_BACKGROUND_COLOR,
};

const NODE_RING: Record<JourneyNodeKind, string> = {
  [JourneyNodeKind.Endpoint]: CRC_CORE_BACKGROUND_COLOR,
  [JourneyNodeKind.Stop]: CRC_TEXT_MUTED,
  [JourneyNodeKind.Ring]: CRC_ACCENT,
};

const ALIGN_TEXT: Record<CresultcardAlign, CSSProperties["textAlign"]> = {
  [CresultcardAlign.Start]: "start",
  [CresultcardAlign.End]: "end",
};

const ALIGN_ITEMS: Record<CresultcardAlign, CSSProperties["alignItems"]> = {
  [CresultcardAlign.Start]: "flex-start",
  [CresultcardAlign.End]: "flex-end",
};

function nodeStyle(kind: JourneyNodeKind): CSSProperties {
  const diameter = NODE_DIAMETER[kind];
  return {
    display: "block",
    width: diameter,
    height: diameter,
    borderRadius: diameter,
    backgroundColor: NODE_FILL[kind],
    boxShadow: `0 0 0 ${String(NODE_RING_WIDTH)}px ${NODE_RING[kind]}`,
  };
}

/* ── Arc maths ────────────────────────────────────────────────────────────── */

function arcXPercent(fraction: number): number {
  return ARC_X_BASE + ARC_X_SPAN * fraction;
}

function arcYPercent(fraction: number): number {
  return ARC_Y_BASE - ARC_Y_LIFT * fraction * (1 - fraction);
}

function percentOf(value: number): string {
  return `${value.toFixed(POSITION_PRECISION)}%`;
}

/** Evenly spaced inside the connector, never on either endpoint. */
function stopFraction(index: number, count: number): number {
  return (index + 1) / (count + 1);
}

/* ── Summary ──────────────────────────────────────────────────────────────── */

/**
 * The stops the graphic cannot draw are folded in here rather than dropped: a
 * six-stop itinerary drawn with three nodes and captioned "3 stops" is a lie
 * the graphic tells on the data's behalf.
 *
 * No stops is `null`, not a word. The only thing this function knows there is
 * that the itinerary has no intermediate stops, and every word for that —
 * "direct", "nonstop" — is an air-travel term that says nothing true about a
 * driving route, a rail leg or a walk. The caller supplies its own if it has
 * one.
 */
export function cresultcardJourneySummary(
  stops: readonly CresultcardJourneyStop[],
): string | null {
  if (stops.length === 0) {
    return null;
  }
  const noun = stops.length === 1 ? SUMMARY_STOP : SUMMARY_STOPS;
  const shown = stops.slice(0, CRC_MAX_GRAPHIC_STOPS);
  const hidden = stops.length - shown.length;
  const codes = shown.map((stop): string => stop.code).join(SUMMARY_JOIN);
  const tail =
    hidden === 0
      ? codes
      : `${codes}${SUMMARY_JOIN}${SUMMARY_PLUS}${String(hidden)} ${SUMMARY_MORE}`;
  return `${String(stops.length)} ${noun}${SUMMARY_SEPARATOR}${tail}`;
}

interface JourneySummaryTextInput {
  readonly summary?: CresultcardJourneySummarySpec;
  readonly stops: readonly CresultcardJourneyStop[];
}

/** The caller's sentence, the stops' own count, or nothing at all. */
function journeySummaryText({
  summary,
  stops,
}: JourneySummaryTextInput): string | null {
  if (summary == null) {
    return cresultcardJourneySummary(stops);
  }
  return summary.source === CresultcardJourneySummarySource.Stated
    ? summary.text
    : null;
}

/* ── Endpoint ─────────────────────────────────────────────────────────────── */

interface JourneyEndpointProps {
  readonly endpoint: CresultcardJourneyEndpoint;
  readonly align: CresultcardAlign;
  /** Rendered as a superscript on the time it modifies, never on its own line. */
  readonly dayOffset?: number;
  readonly dense: boolean;
}

function CresultcardJourneyEndpointBlock({
  endpoint,
  align,
  dayOffset = 0,
  dense,
}: JourneyEndpointProps): ReactElement {
  const signedOffset =
    dayOffset > 0
      ? `${DAY_OFFSET_PLUS}${String(dayOffset)}`
      : String(dayOffset);
  const codeStep = dense
    ? CresultcardTextStep.Title
    : CresultcardTextStep.Lead;
  const textAlign = ALIGN_TEXT[align];

  return (
    <span
      data-el="Cresultcard/JourneyEndpointBlock/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: ALIGN_ITEMS[align],
        gap: CODE_GAP,
        minWidth: 0,
      }}
    >
      <span
        style={{
          ...CRC_TYPE_STYLE[codeStep],
          ...CRC_NUMERIC_STYLE,
          // A three-letter code has nothing to break, so this changes nothing
          // for one. What it fixes is the endpoint that is NOT a code: a
          // 20-character coordinate at the lead step used to refuse to yield and
          // took the column with it.
          overflowWrap: "anywhere",
          minWidth: 0,
          textAlign,
        }}
      >
        {endpoint.code === "" ? EMPTY_CODE : endpoint.code}
      </span>
      {endpoint.time != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Body],
            ...CRC_NUMERIC_STYLE,
            color: CRC_TEXT,
            whiteSpace: "nowrap",
            textAlign,
          }}
        >
          {endpoint.time}
          {dayOffset !== 0 && (
            <sup
              style={{ ...CRC_NUMERIC_STYLE, color: CRC_ACCENT }}
            >
              {signedOffset}
            </sup>
          )}
        </span>
      )}
      {endpoint.name != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            minWidth: 0,
            textAlign,
          }}
        >
          {endpoint.name}
        </span>
      )}
      {endpoint.date != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            ...CRC_NUMERIC_STYLE,
            whiteSpace: "nowrap",
            textAlign,
          }}
        >
          {endpoint.date}
        </span>
      )}
      {endpoint.sublabel != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            color: CRC_TEXT_FAINT,
            minWidth: 0,
            textAlign,
          }}
        >
          {endpoint.sublabel}
        </span>
      )}
    </span>
  );
}

/* ── Centre measure ───────────────────────────────────────────────────────── */

interface JourneyCentreProps {
  readonly centre: CresultcardJourneyCentre;
}

function CresultcardJourneyCentreBlock({
  centre,
}: JourneyCentreProps): ReactElement {
  const CentreGlyph = centre.glyph;
  return (
    <span
      data-el="Cresultcard/JourneyCentreBlock/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: CODE_GAP,
        minWidth: 0,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: CENTRE_GAP,
          color: CRC_TEXT,
        }}
      >
        <CentreGlyph size={CENTRE_GLYPH_SIZE} stroke={GLYPH_STROKE} />
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Body],
            ...CRC_NUMERIC_STYLE,
            color: CRC_TEXT,
            whiteSpace: "nowrap",
          }}
        >
          {centre.primary}
        </span>
      </span>
      {centre.secondary != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            // One line, and BOUNDED by the block it sits in. The micro-line is
            // a family's own composition — `1 stop · CLT · 32 min` today, three
            // named ground times tomorrow — and `nowrap` inside a `min-width: 0`
            // column with no guard is how a 236px rail card gets spilled by a
            // fixture nobody has shipped yet. It keeps the single line it is
            // designed as and gives up its tail instead of the card's edge.
            ...CRC_TRUNCATE_STYLE,
            maxWidth: "100%",
          }}
        >
          {centre.secondary}
        </span>
      )}
    </span>
  );
}

/* ── Connectors ───────────────────────────────────────────────────────────── */

interface JourneyConnectorProps {
  readonly stops: readonly CresultcardJourneyStop[];
  readonly thumb?: CresultcardJourneyThumb;
  readonly closing?: CresultcardJourneyClosing;
  readonly dense: boolean;
}

function CresultcardJourneyArcConnector({
  stops,
  thumb,
  closing,
  dense,
}: JourneyConnectorProps): ReactElement {
  const graphicStops = stops.slice(0, CRC_MAX_GRAPHIC_STOPS);
  const ThumbGlyph = thumb?.glyph;
  const thumbSize = dense ? THUMB_SIZE_COMPACT : THUMB_SIZE;

  return (
    <span
      aria-hidden
      data-el="Cresultcard/JourneyArcConnector/Root"
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        height: dense ? ARC_HEIGHT_COMPACT : ARC_HEIGHT,
      }}
    >
      <svg
        preserveAspectRatio="none"
        style={{ display: "block", width: "100%", height: "100%" }}
        viewBox={ARC_VIEWBOX}
      >
        <path
          className="cresultcard__path"
          d={ARC_PATH}
          fill="none"
          stroke={CRC_TEXT_FAINT}
          strokeLinecap="round"
          strokeWidth={CONNECTOR_STROKE}
          vectorEffect={NON_SCALING_STROKE}
        />
      </svg>
      {graphicStops.map((stop, index): ReactElement => {
        const fraction = stopFraction(index, graphicStops.length);
        return (
          <span
            key={`${stop.code}-${String(index)}`}
            style={{
              ...nodeStyle(JourneyNodeKind.Stop),
              position: "absolute",
              insetInlineStart: percentOf(arcXPercent(fraction)),
              insetBlockStart: percentOf(arcYPercent(fraction)),
              transform: "translate3d(-50%, -50%, 0)",
            }}
          />
        );
      })}
      {ThumbGlyph != null && thumb != null && (
        <span
          className={THUMB_CLASS}
          style={{
            position: "absolute",
            insetInlineStart: percentOf(arcXPercent(thumb.fraction)),
            insetBlockStart: percentOf(arcYPercent(thumb.fraction)),
            display: "inline-flex",
            paddingInline: THUMB_PAD,
            backgroundColor: CRC_CORE_BACKGROUND_COLOR,
            color: CRC_ACCENT,
          }}
        >
          <ThumbGlyph size={thumbSize} stroke={GLYPH_STROKE} />
        </span>
      )}
      {closing != null && (
        <span
          style={{
            ...nodeStyle(JourneyNodeKind.Ring),
            position: "absolute",
            insetInlineStart: percentOf(arcXPercent(1)),
            insetBlockStart: percentOf(arcYPercent(1)),
            transform: "translate3d(-50%, -50%, 0)",
          }}
        />
      )}
    </span>
  );
}

function CresultcardJourneyLineConnector({
  stops,
  thumb,
  closing,
  dense,
}: JourneyConnectorProps): ReactElement {
  const graphicStops = stops.slice(0, CRC_MAX_GRAPHIC_STOPS);
  const ThumbGlyph = thumb?.glyph;
  const thumbSize = dense ? THUMB_SIZE_COMPACT : THUMB_SIZE;

  return (
    <span
      aria-hidden
      data-el="Cresultcard/JourneyLineConnector/Root"
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        minWidth: 0,
        height: dense ? LINE_HEIGHT_COMPACT : LINE_HEIGHT,
      }}
    >
      <span
        className="cresultcard__fill"
        style={{
          position: "absolute",
          insetInline: 0,
          insetBlockStart: "50%",
          height: CONNECTOR_WIDTH,
          backgroundColor: CRC_TEXT_FAINT,
        }}
      />
      {graphicStops.map((stop, index): ReactElement => (
        <span
          key={`${stop.code}-${String(index)}`}
          style={{
            ...nodeStyle(JourneyNodeKind.Stop),
            position: "absolute",
            insetInlineStart: percentOf(
              stopFraction(index, graphicStops.length) * PERCENT,
            ),
            insetBlockStart: "50%",
            transform: "translate3d(-50%, -50%, 0)",
          }}
        />
      ))}
      {ThumbGlyph != null && thumb != null && (
        <span
          className={THUMB_CLASS}
          style={{
            position: "absolute",
            insetInlineStart: percentOf(thumb.fraction * PERCENT),
            insetBlockStart: "50%",
            display: "inline-flex",
            paddingInline: THUMB_PAD,
            backgroundColor: CRC_CORE_BACKGROUND_COLOR,
            color: CRC_ACCENT,
          }}
        >
          <ThumbGlyph size={thumbSize} stroke={GLYPH_STROKE} />
        </span>
      )}
      {closing != null && (
        <span
          style={{
            ...nodeStyle(JourneyNodeKind.Ring),
            position: "absolute",
            insetInlineStart: "100%",
            insetBlockStart: "50%",
            transform: "translate3d(-50%, -50%, 0)",
          }}
        />
      )}
    </span>
  );
}

/* ── The rung ladder ──────────────────────────────────────────────────────── */

interface JourneyRungRowProps {
  readonly kind: JourneyNodeKind;
  readonly primary: ReactNode;
  readonly trailing?: ReactNode;
  /** The rail is drawn per row, so it starts and stops ON the outer nodes. */
  readonly first: boolean;
  readonly last: boolean;
}

function CresultcardJourneyRungRow({
  kind,
  primary,
  trailing,
  first,
  last,
}: JourneyRungRowProps): ReactElement {
  return (
    <div
      data-el="Cresultcard/JourneyRungRow/Root"
      style={{
        display: "grid",
        gridTemplateColumns: `${String(RAIL_WIDTH)}px minmax(0, 1fr) auto`,
        alignItems: "center",
        gap: ENDPOINT_GAP,
        minWidth: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: RAIL_WIDTH,
          alignSelf: "stretch",
        }}
      >
        <span
          style={{
            position: "absolute",
            // Half a row above the first node and half a row below the last is
            // travel that did not happen; the rail stops on the outer nodes.
            insetBlockStart: first ? "50%" : -RUNG_GAP,
            insetBlockEnd: last ? "50%" : -RUNG_GAP,
            insetInlineStart: (RAIL_WIDTH - CONNECTOR_WIDTH) / 2,
            width: CONNECTOR_WIDTH,
            backgroundColor: CRC_TEXT_FAINT,
          }}
        />
        <span
          style={{ ...nodeStyle(kind), position: "relative" }}
        />
      </span>
      <span
        style={{ display: "block", minWidth: 0 }}
      >
        {primary}
      </span>
      <span
        style={{ display: "block", textAlign: "end" }}
      >
        {trailing}
      </span>
    </div>
  );
}

/* ── The journey ──────────────────────────────────────────────────────────── */

interface CresultcardJourneyProps {
  readonly from: CresultcardJourneyEndpoint;
  readonly to: CresultcardJourneyEndpoint;
  readonly shape: CresultcardJourneyShape;
  readonly stops: readonly CresultcardJourneyStop[];
  readonly centre: CresultcardJourneyCentre;
  /**
   * The line beneath the graphic. OMITTED counts the stops, which is meaningful
   * for any journey that has them and is silent for one that does not.
   */
  readonly summary?: CresultcardJourneySummarySpec;
  /** The arrival's day offset, as a superscript on the arrival time. */
  readonly dayOffset?: number;
  readonly thumb?: CresultcardJourneyThumb;
  readonly closing?: CresultcardJourneyClosing;
  readonly compact?: boolean;
}

export function CresultcardJourney({
  from,
  to,
  shape,
  stops,
  centre,
  summary,
  dayOffset = 0,
  thumb,
  closing,
  compact = false,
}: CresultcardJourneyProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const dense = compact || isMobile;
  const summaryText = journeySummaryText({ stops, summary });
  const graphicStops = stops.slice(0, CRC_MAX_GRAPHIC_STOPS);
  const endpoints = `${from.code}${LABEL_TO}${to.code}`;
  const journeyLabel =
    summaryText == null
      ? endpoints
      : `${endpoints}${SUMMARY_SEPARATOR}${summaryText}`;
  const rootStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: CRC_STACK_GAP[CresultcardStackDensity.Snug],
    width: "100%",
    minWidth: 0,
  };

  // A round trip states that it closes even when nothing summarises the stops,
  // so the two parts are joined rather than nested.
  const summaryLineText = [summaryText, closing?.label]
    .filter((part): part is string => part != null)
    .join(SUMMARY_SEPARATOR);

  const summaryLine =
    summaryLineText === "" ? null : (
      <span
        style={{
          ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
          textAlign: "start",
        }}
      >
        {summaryLineText}
      </span>
    );

  if (shape === CresultcardJourneyShape.Rung) {
    return (
      <div
        aria-label={journeyLabel}
        data-el="Cresultcard/Journey/Root"
        role="group"
        style={rootStyle}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: RUNG_GAP,
            minWidth: 0,
          }}
        >
          <CresultcardJourneyRungRow
            first
            kind={JourneyNodeKind.Endpoint}
            last={false}
            primary={
              <CresultcardJourneyEndpointBlock
                align={CresultcardAlign.Start}
                dense={dense}
                endpoint={from}
              />
            }
          />
          {graphicStops.map((stop, index): ReactElement => (
            <CresultcardJourneyRungRow
              first={false}
              key={`${stop.code}-${String(index)}`}
              kind={JourneyNodeKind.Stop}
              last={false}
              primary={
                <span
                  style={{
                    ...CRC_TYPE_STYLE[CresultcardTextStep.Body],
                    minWidth: 0,
                  }}
                >
                  {stop.name == null
                    ? stop.code
                    : `${stop.code}${SUMMARY_SEPARATOR}${stop.name}`}
                </span>
              }
              trailing={
                stop.duration == null ? undefined : (
                  <span
                    style={{
                      ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
                      ...CRC_NUMERIC_STYLE,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stop.duration}
                  </span>
                )
              }
            />
          ))}
          <CresultcardJourneyRungRow
            first={false}
            kind={
              closing == null ? JourneyNodeKind.Endpoint : JourneyNodeKind.Ring
            }
            last
            primary={
              <CresultcardJourneyEndpointBlock
                align={CresultcardAlign.Start}
                dayOffset={dayOffset}
                dense={dense}
                endpoint={to}
              />
            }
          />
        </div>
        <CresultcardJourneyCentreBlock
          centre={centre}
        />
        {summaryLine}
      </div>
    );
  }

  if (shape === CresultcardJourneyShape.Line) {
    return (
      <div
        aria-label={journeyLabel}
        data-el="Cresultcard/Journey/Root"
        role="group"
        style={rootStyle}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: ENDPOINT_GAP,
            minWidth: 0,
          }}
        >
          <CresultcardJourneyEndpointBlock
            align={CresultcardAlign.Start}
            dense={dense}
            endpoint={from}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: CODE_GAP,
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: 0,
              minWidth: 0,
            }}
          >
            <CresultcardJourneyCentreBlock
              centre={centre}
            />
            <CresultcardJourneyLineConnector
              closing={closing}
              dense={dense}
              stops={stops}
              thumb={thumb}
            />
          </div>
          <CresultcardJourneyEndpointBlock
            align={CresultcardAlign.End}
            dayOffset={dayOffset}
            dense={dense}
            endpoint={to}
          />
        </div>
        {summaryLine}
      </div>
    );
  }

  return (
    <div
      aria-label={journeyLabel}
      data-el="Cresultcard/Journey/Root"
      role="group"
      style={rootStyle}
    >
      <CresultcardJourneyCentreBlock
        centre={centre}
      />
      <CresultcardJourneyArcConnector
        closing={closing}
        dense={dense}
        stops={stops}
        thumb={thumb}
      />
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: ENDPOINT_GAP,
          minWidth: 0,
        }}
      >
        <CresultcardJourneyEndpointBlock
          align={CresultcardAlign.Start}
          dense={dense}
          endpoint={from}
        />
        <CresultcardJourneyEndpointBlock
          align={CresultcardAlign.End}
          dayOffset={dayOffset}
          dense={dense}
          endpoint={to}
        />
      </div>
      {summaryLine}
    </div>
  );
}
